<template>
  <div>
    <div
      class="price-range-container"
      :class="classes"
    >
      <div ref="rangeSlider" />
      <div
        v-if="showLabels"
        class="pricing-labels"
      >
        <p
          id="priceRangeMin"
          class="pricing-label"
        >
          {{ startPrice }}
        </p>
        <p
          id="priceRangeMax"
          class="pricing-label"
        >
          {{ endPrice }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import 'nouislider/dist/nouislider.css';

import noUiSlider from 'nouislider';
import { mapState } from 'vuex';

export default {
  name: 'PriceSliderInput',
  props: {
    value: {
      type: Array,
      required: true,
    },
    priceMethod: {
      type: String,
      default: null,
    },
    theme: {
      type: String,
      default: '',
      validator: theme => theme === '' || theme === 'new-filters',
    },
    showLabels: {
      type: Boolean,
      default: true,
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      immediateValue: this.value,
      defaultSettings: {
        step: 10,
        range: {
          min: 0,
          '20%': [100, 25],
          '40%': [500, 50],
          '60%': [1000, 100],
          '80%': [5000, 250],
          max: this.$store.state.priceBoundaries.default[1],
        },
      },
      perPersonSettings: {
        step: 1,
        range: {
          min: this.$store.state.priceBoundaries.per_person[0],
          '50%': [100, 5],
          max: this.$store.state.priceBoundaries.per_person[1],
        },
      },
    };
  },
  computed: {
    ...mapState([
      'l10n',
    ]),
    startPrice() {
      return `${this.l10n.currencySign}${this.immediateValue[0]}`;
    },
    endPrice() {
      let price = this.immediateValue[1];

      if (price === this.currentPriceMethodSettings.range.max) {
        price += '+';
      }

      return `${this.l10n.currencySign}${price}`;
    },
    currentPriceMethodSettings() {
      if (this.priceMethod === 'per_person') {
        return this.perPersonSettings;
      }

      return this.defaultSettings;
    },
    classes() {
      let result = '';

      if (this.noMargin) {
        result += 'price-range-container--no-margin';
      }

      if (this.theme === 'new-filters') {
        result += ' price-range-container--new-filters';
      }
      return result;
    },
  },
  watch: {
    value([priceFrom, priceTo]) {
      if (priceFrom !== this.immediateValue[0] || priceTo !== this.immediateValue[1]) {
        this.setSliderValues([priceFrom, priceTo]);
      }
    },
    immediateValue(value) {
      this.$emit('input', value);
    },
    priceMethod() {
      this.reloadNoUiSlider();
    },
  },
  methods: {
    setSliderValues(priceRange) {
      this.immediateValue = [...priceRange];
      this.$refs.rangeSlider.noUiSlider.set([...priceRange]);
    },
    getPricings() {
      const prices = this.$refs.rangeSlider.noUiSlider.get();
      const priceStart = parseInt(prices[0], 10);
      const priceEnd = parseInt(prices[1], 10);

      return [priceStart, priceEnd];
    },
    onSliderChange() {
      console.log(this.getPricings());
      this.$emit('changed', this.getPricings());
    },
    onSliderUpdate() {
      this.immediateValue = this.getPricings();
    },
    getNoUiSliderOptions() {
      return {
        ...this.currentPriceMethodSettings,
        start: [this.value[0], this.value[1]],
        connect: true,
        behaviour: 'tap',
      };
    },
    reloadNoUiSlider() {
      if (this.$refs.rangeSlider.noUiSlider) {
        this.$refs.rangeSlider.noUiSlider.destroy();
      }

      noUiSlider.create(this.$refs.rangeSlider, this.getNoUiSliderOptions());

      this.$refs.rangeSlider.noUiSlider.on('change', this.onSliderChange);
      this.$refs.rangeSlider.noUiSlider.on('update', this.onSliderUpdate);

      // set initial immediate value
      this.onSliderUpdate();
    },
  },
  mounted() {
    this.reloadNoUiSlider();
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables";

$height: 4px;

.price-range-container {
  margin: 17px 30px 0;

  &.price-range-container--no-margin {
    margin: 0;
    padding: 10px 12px 10px 6px;
    position: relative;
  }

  :global(.noUi-handle) {
    touch-action: none;
  }
}

.price-range-container--new-filters {
  &::before,
  &::after {
    position: absolute;
    content: "";
    height: $height;
    width: 14px;
    background: variables.$grey-400;
    border-radius: 4px;
    box-shadow: inset 0 1px 1px #f0f0f0;
    top: calc(50% - #{calc($height/2)});
  }

  :global(.noUi-horizontal) {
    height: $height;
    border-radius: 0;
    box-shadow: none;
    border: 0;
  }

  :global(.noUi-connects) {
    background: variables.$grey-400;
  }
  :global(.noUi-connect) {
    background: variables.$green;
  }
  :global(.noUi-horizontal .noUi-handle) {
    border: none;
    width: 24px;
    height: 24px;
    background-color: variables.$green;
    box-shadow: none;
    border-radius: 100%;

    background-image: url('/build/images/search/handle.svg');
    background-repeat: no-repeat;
    background-size: 12px 12px;
    background-position: center;
    top: -10px;

    cursor: pointer;
  }
  :global(.noUi-handle::after),
  :global(.noUi-handle::before) {
    display: none;
  }
}
</style>
